/**
 * `WorldState` — the complete truth of a game at the start of a turn (§9.1).
 *
 * Two hard constraints:
 *
 *  1. **JSON-round-trippable.** It is written to `turn_snapshots.world_state` as
 *     JSONB. No `Map`, no `Set`, no `Date`, no `BigInt`, no `undefined` where an
 *     absent key would do. `test/world.test.ts` asserts the round trip.
 *  2. **Immutable.** Phases never mutate it; they emit `StateMutation`s that are
 *     folded at the end of the phase (§7.2).
 *
 * Fields that only M3 and M4 populate (`debris`, `weather`, `escalation`) are
 * declared *now*, empty. Adding a top-level key later invalidates every committed
 * golden snapshot; adding it now costs one line.
 */
export const WORLD_SCHEMA_VERSION = 1;
export function emptyEscalationState(start = 0) {
    return {
        counter: start,
        baseline: start,
        entries: [],
        quietTurns: 0,
        decayTotal: 0,
        roeViolations: { BLUFOR: 0, REDFOR: 0 },
        debrisGeneratedBy: { BLUFOR: 0, REDFOR: 0 },
        benignEngagements: { BLUFOR: 0, REDFOR: 0 },
    };
}
export const ESCALATION_LEVEL_BANDS = Object.freeze([
    { level: 'UNCONTROLLED', atOrAbove: 21 },
    { level: 'CONFLICT', atOrAbove: 13 },
    { level: 'CRISIS', atOrAbove: 6 },
    { level: 'COMPETITION', atOrAbove: 0 },
]);
/** RB §7.1. Derived, never stored. */
export function escalationLevel(counter) {
    for (const band of ESCALATION_LEVEL_BANDS) {
        if (counter >= band.atOrAbove)
            return band.level;
    }
    return 'COMPETITION';
}
export const EMPTY_CYBER_STATE = Object.freeze({
    footholds: {},
    access: {},
    persistence: {},
    isolated: Object.freeze([]),
});
export function cyberKey(team, subject) {
    return `${team}:${subject}`;
}
export function accessOf(cyber, team, asset) {
    const perAsset = cyber.access[cyberKey(team, asset)];
    if (perAsset)
        return perAsset;
    return 'NO_ACCESS';
}
export function hasFoothold(cyber, team, network) {
    return cyber.footholds[cyberKey(team, network)] === true;
}
export const DEFAULT_MW_OMISSION = Object.freeze({
    chance: 0.5,
    scope: 'PER_STAGE',
    select: 'RANDOM',
});
export const DEFAULT_WORLD_CONFIG = Object.freeze({
    searchPersistenceBonus: 0,
    searchPersistenceMaxTurns: 3,
    mwOmission: DEFAULT_MW_OMISSION,
});
/**
 * Hardening → base TN for every cyber action against it (RB §5.6.1).
 *
 * Lives here rather than in `data/cyber.ts` because `deriveStatus`-adjacent
 * helpers should not force the engine to import a content table, and because the
 * value is a property of the vocabulary rather than a tunable.
 */
export const CYBER_BASE_TN = Object.freeze({
    VULNERABLE: 8,
    NORMAL: 11,
    HARDENED: 14,
    MONITORED: 14,
});
export function assetsOf(world, team) {
    return Object.values(world.assets)
        .filter((a) => a.owner === team)
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
export function getAsset(world, id) {
    return world.assets[id];
}
/**
 * Constellation health (§5.3.4.1, §5.7.6). Thresholds are scenario-defined
 * (errata #20); counting operational members is the engine's job.
 */
export function constellationHealth(operational, threshold) {
    if (operational >= threshold.nominalAtOrAbove)
        return 'NOMINAL';
    if (operational >= threshold.degradedAtOrAbove)
        return 'DEGRADED';
    return 'OFFLINE';
}
//# sourceMappingURL=world.js.map